// Mixpanel is scoped to the student trial funnel only: from the signup form to
// booking and joining the trial class. Every call here is a no-op unless both of these
// hold, and a tracking failure can never block signup or booking:
//
//   1. NEXT_PUBLIC_MIXPANEL_TOKEN is set.
//   2. This is a production build (isProd), or the isMixpanelDebugEnabled
//      feature flag is on (local testing).
//
// Our users are children, so we never send names, phones, emails, dates of
// birth, IP-derived location or real URLs: funnel URLs can carry `ph`, and
// some paths are themselves secret (the trial short link `/<slug>`), so only
// the route template (e.g. `/[slug]`) is sent. The distinct id is our internal
// user id.

import type { UserProps } from '@contexts/session'
import { SUBJECTS } from '@lib/constants/billing'
import { getTrialSubject } from '@lib/constants/booking'
import { isMixpanelDebugEnabled } from '@lib/utils/feature-flag'
import type { Config, OverridedMixpanel } from 'mixpanel-browser'
import Router from 'next/router'

// Properties the SDK adds by default that we must not send. Full URLs and
// referrers can contain `ph` (phone) query params, and raw ad click ids are
// replaced by our own has_gclid / has_fbclid flags.
export const MIXPANEL_BLOCKED_DEFAULT_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$initial_referrer',
  'current_page_title',
  'current_url_search',
  'dclid',
  'fbclid',
  'gclid',
  'ko_click_id',
  'li_fat_id',
  'msclkid',
  'sccid',
  'ttclid',
  'twclid',
  'wbraid',
]

// Event names follow `web:<page>:[student:][<popup>:]<action>`. Actions:
// visit, click_*, select_*, view_error_* (a validation message was shown),
// request_error (an API call failed; see `reason`), answer_saved and
// booking_blocked (the lead cannot go on to book; see `reason`).
export enum MixpanelEvent {
  // Signup form (/register and every landing page that embeds it)
  REGISTER_VISIT = 'web:register:visit_anon_user',
  REGISTER_CLICK_PROCEED = 'web:register:click_proceed',
  REGISTER_VIEW_ERROR_INVALID_FORM = 'web:register:view_error_invalid_form',
  REGISTER_VIEW_ERROR_COUNTRY_BLOCKED = 'web:register:view_error_country_blocked',
  REGISTER_REQUEST_ERROR = 'web:register:request_error',
  REGISTER_DETAILS_SUBMITTED = 'web:register:details_submitted',
  REGISTER_EXISTING_USER_FOUND = 'web:register:existing_user_found',
  REGISTER_SIGNUP_COMPLETED = 'web:register:signup_completed',

  // Phone verification (OTP), /register/verify-otp and /login/verify-otp.
  // Every event carries `flow`: registration or login.
  PHONE_VERIFY_VISIT_AFTER_REGISTRATION = 'web:phone_verify:student:visit_after_registration',
  PHONE_VERIFY_VISIT = 'web:phone_verify:student:visit',
  PHONE_VERIFY_OTP_ENTER = 'web:phone_verify:student:otp_enter',
  PHONE_VERIFY_OTP_SUCCESS = 'web:phone_verify:student:otp_success',
  PHONE_VERIFY_OTP_ERROR = 'web:phone_verify:student:otp_error',
  PHONE_VERIFY_OTP_RESEND_WHATSAPP = 'web:phone_verify:student:otp_resend_whatsapp',
  PHONE_VERIFY_OTP_RESEND_SMS = 'web:phone_verify:student:otp_resend_sms',
  PHONE_VERIFY_OTP_RESEND_CALL = 'web:phone_verify:student:otp_resend_call',
  PHONE_VERIFY_OTP_RESEND_EMAIL = 'web:phone_verify:student:otp_resend_email',

  // Spoken language preference. There is no "No preference" button yet; add
  // web:language_preference:student:click_no_preference when there is one.
  LANGUAGE_PREFERENCE_VISIT = 'web:language_preference:student:visit',
  LANGUAGE_PREFERENCE_SELECT_LANGUAGE = 'web:language_preference:student:select_language',
  LANGUAGE_PREFERENCE_DESELECT_LANGUAGE = 'web:language_preference:student:deselect_language',
  LANGUAGE_PREFERENCE_CLICK_NEXT = 'web:language_preference:student:click_next',
  LANGUAGE_PREFERENCE_VIEW_ERROR_NO_LANGUAGE_SELECTED = 'web:language_preference:student:view_error_no_language_selected',
  LANGUAGE_PREFERENCE_ANSWER_SAVED = 'web:language_preference:student:answer_saved',
  LANGUAGE_PREFERENCE_REQUEST_ERROR = 'web:language_preference:student:request_error',
  LANGUAGE_PREFERENCE_BOOKING_BLOCKED = 'web:language_preference:student:booking_blocked',
  LANGUAGE_PREFERENCE_ENGLISH_MODAL_VISIT = 'web:language_preference:student:english_modal:visit',
  LANGUAGE_PREFERENCE_ENGLISH_MODAL_CLICK_YES = 'web:language_preference:student:english_modal:click_yes_understands_english',
  LANGUAGE_PREFERENCE_ENGLISH_MODAL_CLICK_NOT_YET = 'web:language_preference:student:english_modal:click_not_yet',
  LANGUAGE_PREFERENCE_ENGLISH_MODAL_VISIT_ENGLISH_ONLY = 'web:language_preference:student:english_modal:visit_english_only_message',
  LANGUAGE_PREFERENCE_ENGLISH_MODAL_CLICK_BACK_TO_HOMEPAGE = 'web:language_preference:student:english_modal:click_back_to_homepage',
  LANGUAGE_PREFERENCE_ENGLISH_MODAL_CLOSE = 'web:language_preference:student:english_modal:close',

  // System confirmation ("Do you have a laptop?", mobile only)
  SYSTEM_CONFIRMATION_VISIT = 'web:system_confirmation:student:visit',
  SYSTEM_CONFIRMATION_CLICK_HAS_COMPUTER = 'web:system_confirmation:student:click_has_computer',
  SYSTEM_CONFIRMATION_CLICK_HAS_NO_COMPUTER = 'web:system_confirmation:student:click_has_no_computer',
  SYSTEM_CONFIRMATION_ANSWER_SAVED = 'web:system_confirmation:student:answer_saved',
  SYSTEM_CONFIRMATION_REQUEST_ERROR = 'web:system_confirmation:student:request_error',
  SYSTEM_CONFIRMATION_BOOKING_BLOCKED = 'web:system_confirmation:student:booking_blocked',
  SYSTEM_CONFIRMATION_NO_COMPUTER_MODAL_VISIT = 'web:system_confirmation:student:no_computer_modal:visit',
  SYSTEM_CONFIRMATION_NO_COMPUTER_MODAL_CLICK_GO_TO_HOME = 'web:system_confirmation:student:no_computer_modal:click_go_to_home',
  SYSTEM_CONFIRMATION_NO_COMPUTER_MODAL_CLOSE = 'web:system_confirmation:student:no_computer_modal:close',

  // Purchase intent, and the cards that replace its options
  PURCHASE_INTENT_VISIT = 'web:purchase_intent:student:visit',
  PURCHASE_INTENT_SELECT_INTENT = 'web:purchase_intent:student:select_intent',
  PURCHASE_INTENT_CLICK_PROCEED = 'web:purchase_intent:student:click_proceed',
  PURCHASE_INTENT_VIEW_ERROR_NO_INTENT_SELECTED = 'web:purchase_intent:student:view_error_no_intent_selected',
  PURCHASE_INTENT_ANSWER_SAVED = 'web:purchase_intent:student:answer_saved',
  PURCHASE_INTENT_REQUEST_ERROR = 'web:purchase_intent:student:request_error',
  PURCHASE_INTENT_BOOKING_BLOCKED = 'web:purchase_intent:student:booking_blocked',
  PURCHASE_INTENT_PRICE_CLARIFICATION_VISIT = 'web:purchase_intent:student:price_clarification:visit',
  PURCHASE_INTENT_PRICE_CLARIFICATION_CLICK_CONTINUE = 'web:purchase_intent:student:price_clarification:click_continue',
  PURCHASE_INTENT_PRICE_CLARIFICATION_CLICK_NO_THANKS = 'web:purchase_intent:student:price_clarification:click_no_thanks',
  PURCHASE_INTENT_EXIT_ACKNOWLEDGEMENT_VISIT = 'web:purchase_intent:student:exit_acknowledgement:visit',
  PURCHASE_INTENT_EXIT_ACKNOWLEDGEMENT_CLICK_BOOK_TRIAL_ANYWAY = 'web:purchase_intent:student:exit_acknowledgement:click_book_trial_anyway',
  PURCHASE_INTENT_EXIT_ACKNOWLEDGEMENT_CLICK_GO_TO_HOMEPAGE = 'web:purchase_intent:student:exit_acknowledgement:click_go_to_homepage',
  PURCHASE_INTENT_FOCUS_AREAS_VISIT = 'web:purchase_intent:student:focus_areas:visit',
  PURCHASE_INTENT_FOCUS_AREAS_SELECT_FOCUS_AREA = 'web:purchase_intent:student:focus_areas:select_focus_area',
  PURCHASE_INTENT_FOCUS_AREAS_DESELECT_FOCUS_AREA = 'web:purchase_intent:student:focus_areas:deselect_focus_area',
  PURCHASE_INTENT_FOCUS_AREAS_CLICK_BOOK_FREE_LESSON = 'web:purchase_intent:student:focus_areas:click_book_free_lesson',
  PURCHASE_INTENT_FOCUS_AREAS_CLICK_SKIP_BOOKING = 'web:purchase_intent:student:focus_areas:click_skip_booking_for_now',
  PURCHASE_INTENT_FOCUS_AREAS_VIEW_ERROR_NO_FOCUS_AREA_SELECTED = 'web:purchase_intent:student:focus_areas:view_error_no_focus_area_selected',
  PURCHASE_INTENT_FOCUS_AREAS_ANSWER_SAVED = 'web:purchase_intent:student:focus_areas:answer_saved',
  PURCHASE_INTENT_FOCUS_AREAS_REQUEST_ERROR = 'web:purchase_intent:student:focus_areas:request_error',

  // Grade readiness (Kindergarten, and Grade 1 in some countries)
  GRADE_READINESS_VISIT = 'web:grade_readiness:student:visit',
  GRADE_READINESS_CLICK_READY = 'web:grade_readiness:student:click_ready',
  GRADE_READINESS_CLICK_NOT_READY = 'web:grade_readiness:student:click_not_ready',
  GRADE_READINESS_BOOKING_BLOCKED = 'web:grade_readiness:student:booking_blocked',
  GRADE_NOT_READY_VISIT = 'web:grade_not_ready:student:visit',
  GRADE_NOT_READY_CLICK_NOTIFY = 'web:grade_not_ready:student:click_notify',
  GRADE_NOT_READY_CLICK_CHANGE_GRADE = 'web:grade_not_ready:student:click_change_grade',

  // Trial class booking (/trial-class/booking)
  TRIAL_CLASS_SLOTS_VISIT = 'web:trial_class_slots:student:visit',
  TRIAL_CLASS_SLOTS_CLICK_DATE = 'web:trial_class_slots:student:click_date',
  TRIAL_CLASS_SLOTS_CLICK_TIME = 'web:trial_class_slots:student:click_time',
  TRIAL_CLASS_SLOTS_CLICK_BOOK_NOW = 'web:trial_class_slots:student:click_book_now',
  TRIAL_CLASS_SLOTS_VIEW_ERROR_SELECT_DATE_TIME = 'web:trial_class_slots:student:view_error_select_date_time',
  TRIAL_CLASS_SLOTS_CLICK_CHANGE_GRADE = 'web:trial_class_slots:student:click_change_grade',
  TRIAL_CLASS_SLOTS_CLICK_CHANGE_TIMEZONE = 'web:trial_class_slots:student:click_change_timezone',
  TRIAL_CLASS_SLOTS_CLICK_MORE_SLOT_OPTIONS = 'web:trial_class_slots:student:click_more_slot_options',
  TRIAL_CLASS_SLOTS_CLICK_REQUEST_CALLBACK = 'web:trial_class_slots:student:click_request_callback',
  TRIAL_CLASS_SLOTS_CALLBACK_REQUESTED = 'web:trial_class_slots:student:callback_requested',
  TRIAL_CLASS_SLOTS_REQUEST_ERROR = 'web:trial_class_slots:student:request_error',
  TRIAL_CLASS_SLOTS_BOOKING_BLOCKED = 'web:trial_class_slots:student:booking_blocked',
  TRIAL_CLASS_SLOTS_BOOKING_REQUESTED = 'web:trial_class_slots:student:booking_requested',
  TRIAL_CLASS_SLOTS_TRIAL_BOOKED = 'web:trial_class_slots:student:trial_booked',
  // The 3-second "Booking your free trial now..." popup
  TRIAL_CLASS_SLOTS_CONFIRMATION_MODAL_VISIT = 'web:trial_class_slots:student:confirmation_modal:visit',
  TRIAL_CLASS_SLOTS_CONFIRMATION_MODAL_CLICK_CHOOSE_ANOTHER_TIME = 'web:trial_class_slots:student:confirmation_modal:click_choose_another_time',
  TRIAL_CLASS_SLOTS_CONFIRMATION_MODAL_PRESS_ESCAPE = 'web:trial_class_slots:student:confirmation_modal:press_escape',
  TRIAL_CLASS_SLOTS_CONFIRMATION_MODAL_CLOSE = 'web:trial_class_slots:student:confirmation_modal:close',

  // Trial class join (/trial-class/join), with its reschedule and cancel popups
  TRIAL_CLASS_JOIN_VISIT = 'web:trial_class_join:student:visit',
  TRIAL_CLASS_JOIN_CLICK_RUN_COMPUTER_CHECK = 'web:trial_class_join:student:click_run_computer_check',
  TRIAL_CLASS_JOIN_COMPUTER_CHECK_SUCCESS = 'web:trial_class_join:student:computer_check_success',
  TRIAL_CLASS_JOIN_COMPUTER_CHECK_ERROR = 'web:trial_class_join:student:computer_check_error',
  TRIAL_CLASS_JOIN_CLICK_JOIN_CLASS = 'web:trial_class_join:student:click_join_class',
  TRIAL_CLASS_JOIN_CLICK_TEST_INTERNET_SPEED = 'web:trial_class_join:student:click_test_internet_speed',
  TRIAL_CLASS_JOIN_CLICK_DOWNLOAD_GOOGLE_CHROME = 'web:trial_class_join:student:click_download_google_chrome',
  TRIAL_CLASS_JOIN_CLICK_RESCHEDULE_CLASS = 'web:trial_class_join:student:click_reschedule_class',
  TRIAL_CLASS_JOIN_RESCHEDULE_CLASS_CLICK_DATE = 'web:trial_class_join:student:reschedule_class:click_date',
  TRIAL_CLASS_JOIN_RESCHEDULE_CLASS_CLICK_TIME = 'web:trial_class_join:student:reschedule_class:click_time',
  TRIAL_CLASS_JOIN_RESCHEDULE_CLASS_CLICK_CONFIRM_RESCHEDULING = 'web:trial_class_join:student:reschedule_class:click_confirm_rescheduling',
  TRIAL_CLASS_JOIN_RESCHEDULE_CLASS_CLICK_GO_BACK = 'web:trial_class_join:student:reschedule_class:click_go_back',
  TRIAL_CLASS_JOIN_RESCHEDULE_CLASS_SUCCESS_CLICK_VISIT_DASHBOARD = 'web:trial_class_join:student:reschedule_class:success:click_visit_dashboard',
  TRIAL_CLASS_JOIN_CLICK_CANCEL_CLASS = 'web:trial_class_join:student:click_cancel_class',
  TRIAL_CLASS_JOIN_CANCEL_CLASS_ENTER_REASON = 'web:trial_class_join:student:cancel_class:enter_reason',
  TRIAL_CLASS_JOIN_CANCEL_CLASS_CLICK_CANCEL_CLASS = 'web:trial_class_join:student:cancel_class:click_cancel_class',
  TRIAL_CLASS_JOIN_CANCEL_CLASS_CLICK_GO_BACK = 'web:trial_class_join:student:cancel_class:click_go_back',

  // Trial short link page (/<slug>). Carries class details, never names.
  TRIAL_CLASS_SHORT_LINK_VISIT = 'web:trial_class_short_link:visit',
  TRIAL_CLASS_SHORT_LINK_ENTER_PASSCODE = 'web:trial_class_short_link:enter_passcode',
  TRIAL_CLASS_SHORT_LINK_CLICK_JOIN_CLASS = 'web:trial_class_short_link:click_join_class',
  TRIAL_CLASS_SHORT_LINK_SUCCESSFUL_PHONE_VERIFICATION = 'web:trial_class_short_link:successful_phone_verification',

  // Classroom waiting screen (trial students only)
  CLASSROOM_WAITING_VISIT = 'web:classroom_waiting:student:visit',
}

type MixpanelPropertyValue = string | number | boolean | null | undefined

// Only these keys ever leave the browser. Anything else passed in is dropped,
// so a caller cannot accidentally send personal data.
const ALLOWED_PROPERTY_KEYS = [
  'page_path',
  'entry_source',
  'identifier_type',
  'subject',
  'course',
  'country',
  'zone',
  'grade',
  'has_laptop',
  'signup_source',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'has_gclid',
  'has_fbclid',
  'reason',
  'status_code',
  'fields',
  'choice',
  'language',
  'flow',
  'lesson_title',
  'class_start_time',
  'slot_count',
  'day_zero_slot_count',
  'day_one_slot_count',
  'peak_hour_slot_count',
  'day_zero_date',
  'day_one_date',
  'first_slot_date_time',
  'purchase_intent',
  'slots_response_date_time',
  'in_app_browser',
  'classroom_id',
  'is_paid_user',
] as const

// Values shown to people as-is (e.g. language: 'English'), so not lowercased.
const CASE_PRESERVED_PROPERTY_KEYS = [
  'language',
  'lesson_title',
  'class_start_time',
]

export type MixpanelPropertyKey = typeof ALLOWED_PROPERTY_KEYS[number]
export type MixpanelProperties = Partial<
  Record<MixpanelPropertyKey, MixpanelPropertyValue>
>

/**
 * Keep allowlisted keys and drop empty values. Mixpanel treats '' and null as
 * real values, which pollutes breakdowns, so we omit them instead.
 */
export const sanitizeMixpanelProperties = (
  properties: Record<string, unknown> = {}
): Record<string, string | number | boolean> => {
  const sanitized: Record<string, string | number | boolean> = {}
  Object.entries(properties).forEach(([key, value]) => {
    if (!(ALLOWED_PROPERTY_KEYS as readonly string[]).includes(key)) return
    if (typeof value === 'string') {
      if (value) {
        sanitized[key] = CASE_PRESERVED_PROPERTY_KEYS.includes(key)
          ? value
          : value.toLowerCase()
      }
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      sanitized[key] = value
    }
  })
  return sanitized
}

const MIXPANEL_SUBJECT_BY_NAME: Record<string, string> = {
  // Subject names as the API returns them (e.g. course.subject)
  [SUBJECTS.CODING]: 'coding',
  [SUBJECTS.MATH]: 'math',
  [SUBJECTS.ELA]: 'ela',
  // getTrialSubject's label for ELA (its Coding / Math labels match SUBJECTS)
  'Public Speaking & Communication': 'ela',
}

/**
 * One consistent subject value for breakdowns, from a subject name or a trial
 * course slug. Slugs go through getTrialSubject, so a new Math / ELA course
 * only needs adding there.
 */
export const getMixpanelSubject = (courseOrSubject?: string | null): string => {
  const value = `${courseOrSubject || ''}`.trim()
  return (
    MIXPANEL_SUBJECT_BY_NAME[value] ||
    MIXPANEL_SUBJECT_BY_NAME[getTrialSubject(value)] ||
    'coding'
  )
}

// The route template, e.g. `/[slug]` rather than the real `/<class-link>`.
const getMixpanelPagePath = (): string | undefined => {
  try {
    return Router.pathname
  } catch {
    // The router is not ready yet.
    return undefined
  }
}

export const isMixpanelEnabled = (): boolean => {
  if (typeof window === 'undefined') return false
  if (!process.env.NEXT_PUBLIC_MIXPANEL_TOKEN) return false
  if (isMixpanelDebugEnabled()) return true
  const isProd = process.env.NODE_ENV === 'production'
  return isProd
}

let mixpanelPromise: Promise<OverridedMixpanel | null> | null = null

/**
 * Load and initialise the SDK once, on first use. It is imported dynamically
 * so pages outside the funnel never download it. The core loader leaves out
 * session replay and feature flags, which we do not use (see the SDK README).
 */
const getMixpanel = (): Promise<OverridedMixpanel | null> => {
  if (!isMixpanelEnabled()) return Promise.resolve(null)
  if (mixpanelPromise) return mixpanelPromise

  mixpanelPromise = import('mixpanel-browser/src/loaders/loader-module-core')
    // The loader's type file re-exports the types but not its default export.
    .then(
      (module) =>
        ((module as unknown) as { default?: OverridedMixpanel }).default
    )
    .then((mixpanel) => {
      if (!mixpanel) return null
      const config: Partial<Config> = {
        debug: isMixpanelDebugEnabled(),
        autocapture: false,
        // eslint-disable-next-line @typescript-eslint/camelcase
        record_sessions_percent: 0,
        // eslint-disable-next-line @typescript-eslint/camelcase
        track_pageview: false,
        ip: false,
        // Stops the full first referrer URL being stored and set on profiles.
        // eslint-disable-next-line @typescript-eslint/camelcase
        save_referrer: false,
        // eslint-disable-next-line @typescript-eslint/camelcase
        property_blacklist: MIXPANEL_BLOCKED_DEFAULT_PROPERTIES,
        persistence: 'localStorage',
      }
      mixpanel.init(`${process.env.NEXT_PUBLIC_MIXPANEL_TOKEN}`, config)
      mixpanel.register({ platform: 'web' })
      return mixpanel
    })
    .catch(() => {
      mixpanelPromise = null
      return null
    })

  return mixpanelPromise
}

const runSafely = async (
  callback: (mixpanel: OverridedMixpanel) => void
): Promise<void> => {
  try {
    const mixpanel = await getMixpanel()
    if (mixpanel) callback(mixpanel)
  } catch {
    // Tracking must never break the funnel.
  }
}

export const trackMixpanelEvent = (
  event: MixpanelEvent,
  properties: MixpanelProperties = {}
): Promise<void> =>
  runSafely((mixpanel) =>
    mixpanel.track(event, {
      ...sanitizeMixpanelProperties(properties),
      // eslint-disable-next-line @typescript-eslint/camelcase
      page_path: getMixpanelPagePath(),
    })
  )

/**
 * Link this browser to the user and, at signup only, set their profile.
 * Profile properties must go through here, never through track.
 */
export const identifyMixpanelUser = (
  userId?: number | string,
  profile: MixpanelProperties = {}
): Promise<void> => {
  if (!userId) return Promise.resolve()
  return runSafely((mixpanel) => {
    mixpanel.identify(`${userId}`)
    const sanitizedProfile = sanitizeMixpanelProperties(profile)
    if (Object.keys(sanitizedProfile).length) {
      mixpanel.people.set(sanitizedProfile)
    }
  })
}

/**
 * The user's attributes that rarely or never change. They are registered as
 * super properties (see syncMixpanelUser), so every event can be filtered by
 * them. `is_paid_user` splits trial bookings by paid students (cross-sell
 * trials) from new leads.
 */
export const getMixpanelUserProperties = (
  user: UserProps
): MixpanelProperties => ({
  grade: user?.student?.grade,
  country: user?.country,
  zone: user?.zone,
  // eslint-disable-next-line @typescript-eslint/camelcase
  has_laptop: user?.hasLaptop,
  // eslint-disable-next-line @typescript-eslint/camelcase
  is_paid_user: Boolean(user?.isPaidUser),
  // eslint-disable-next-line @typescript-eslint/camelcase
  signup_source: user?.signupSource,
  // eslint-disable-next-line @typescript-eslint/camelcase
  utm_source: user?.leadSource?.utmSource,
  // eslint-disable-next-line @typescript-eslint/camelcase
  utm_medium: user?.leadSource?.utmMedium,
  // eslint-disable-next-line @typescript-eslint/camelcase
  utm_campaign: user?.leadSource?.utmCampaign,
})

/**
 * Identify the user by id and register their attributes as super properties,
 * which the SDK stores on this device and adds to every later event. Called on
 * every funnel page load and at signup, so a value that changes (a new grade
 * or country) is corrected on the next page. register() is local only; it
 * sends no request. An attribute that is now empty is unregistered, so an old
 * value is never left behind.
 */
export const syncMixpanelUser = (user: UserProps): Promise<void> => {
  if (!user?.id) return Promise.resolve()
  return runSafely((mixpanel) => {
    mixpanel.identify(`${user.id}`)
    const properties = getMixpanelUserProperties(user)
    const sanitized = sanitizeMixpanelProperties(properties)
    mixpanel.register(sanitized)
    Object.keys(properties)
      .filter((key) => !(key in sanitized))
      .forEach((key) => mixpanel.unregister(key))
  })
}

/**
 * Identify the new user, set their profile once, register their attributes,
 * then track the signup. Shared by every signup form so the properties stay
 * the same. Not awaited by callers: tracking must not delay the redirect.
 */
export const trackMixpanelSignupCompleted = (
  user: UserProps,
  properties: MixpanelProperties = {}
): Promise<void> => {
  const profile = getMixpanelUserProperties(user)
  return identifyMixpanelUser(user?.id, profile)
    .then(() => syncMixpanelUser(user))
    .then(() =>
      trackMixpanelEvent(MixpanelEvent.REGISTER_SIGNUP_COMPLETED, {
        ...properties,
        ...profile,
      })
    )
}

/** Clear the stored identity so the next person on this device starts fresh. */
export const resetMixpanelUser = (): Promise<void> =>
  runSafely((mixpanel) => mixpanel.reset())

// Visible for tests.
export const resetMixpanelClientForTests = (): void => {
  mixpanelPromise = null
}
